import { Button, ButtonProps, Col, Divider, Input, message, Modal, Row, Upload } from "antd";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CloseCircleOutlined, UploadOutlined } from "@ant-design/icons";
import { CoverageConfirmation, NegotiationModel } from "../../types/negotiations";
import { SubmissionStates } from "./SelectSurveyPageCard";
import { UploadDoc } from "../documents/UploadDocumentModal";
import {
  useCreateCoverageConfirmationTransitionMutation,
  useUpdateCoverageConfirmationDocumentMutation,
  useUploadCoverageConfirmationDocumentMutation
} from "../../redux/api/coverageConfirmationApiSlice";
import axios from "axios";
import { States } from "../../types";

export default function ConfirmSubmissionButton({ negotiation, coverageConfirmation, buttonProps }: {
  negotiation: NegotiationModel,
  coverageConfirmation: CoverageConfirmation,
  buttonProps?: ButtonProps
}) {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [currentNote, setCurrentNote] = useState<string>("");
  const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>(false);

  const [docList, setDocList] = useState<UploadDoc[]>([]);
  const [realFiles, setRealFiles] = useState<File[][]>([]);
  const [filesTooLong, setFilesTooLong] = useState<string[]>([]);
  const maxFileSize = 50 * 1024 * 1024; // 50 MB

  const [createTransition] = useCreateCoverageConfirmationTransitionMutation();
  const [uploadCoverageDocument] = useUploadCoverageConfirmationDocumentMutation();
  const [updateDocument] = useUpdateCoverageConfirmationDocumentMutation();

  const currentVariables = useMemo(() => {
    const state = coverageConfirmation.submission_state;
    let currentVariables = { text: "", type: "" };
    if (state === SubmissionStates.RECEIVED || state === SubmissionStates.OTHER_INFO)
      currentVariables = { text: "Lettera di quotazione", type: "QTLTT" };
    if (state === SubmissionStates.QUOTATION_SENT)
      currentVariables = { text: "Conferma di copertura", type: "CVRLTT" };
    return currentVariables;
  }, [coverageConfirmation.submission_state]);

  useEffect(() => {
    let disabled: boolean = false;
    if (coverageConfirmation.submission_state === SubmissionStates.QUOTATION_SENT && negotiation.state !== States.Bound)
      disabled = true;
    if (coverageConfirmation.submission_state === SubmissionStates.CONFIRMATION_SENT)
      disabled = true;
    setIsButtonDisabled(disabled);
  }, [coverageConfirmation.submission_state, negotiation.state]);

  function handleOnChange(files: any) {
    const filesToUpload = files.filter((el: any) => el.size <= maxFileSize);
    let newFiles = filesToUpload.map((el: any) => el.originFileObj);
    setRealFiles([newFiles]);
  }

  const saveInAws = useCallback((data: any, file: File) => {
    const postData = new FormData();
    for (const key in data.fields)
      postData.append(key, data.fields[key]);
    postData.append("file", file);
    return axios.post(data.url, postData);
  }, []);

  const uploadDocuments = async () => {
    const fileInfo = docList[0];
    const realFile = realFiles[0];
    const state = coverageConfirmation.submission_state;
    if (fileInfo && fileInfo.fileName && realFile[0].type && coverageConfirmation.uuid && currentVariables.type) {
      try {
        const responseSaveDocs = await uploadCoverageDocument({
          coverageConfirmationUuid: coverageConfirmation.uuid,
          name: fileInfo.fileName,
          content_type: realFile[0].type,
          coverage_confirmation: coverageConfirmation.uuid,
          type: currentVariables.type
        }).unwrap();
        await saveInAws(responseSaveDocs, realFile[0]);
        await updateDocument({
          document_id: responseSaveDocs.uuid,
          is_valid: true,
          coverage_confirmation: coverageConfirmation.uuid
        }).unwrap();
        createTransition({
          coverageConfirmationUuid: coverageConfirmation.uuid,
          state_cc_to: state === "0050" ? "0200" : "0050",
          notes: currentNote
        });
        setDocList([]);
        setRealFiles([]);
        setCurrentNote("");
        setIsModalOpen(false);
      } catch (err) {
        console.log(err);
      }
    } else {
      message.error("File compromesso");
    }
  };

  const onConfirmModal = () => {
    void uploadDocuments();
  };

  const onCloseModal = () => {
    setDocList([]);
    setRealFiles([]);
    setCurrentNote("");
    setIsModalOpen(false);
  };

  return <>
    <Modal open={isModalOpen}
           title={"Conferma Submission"}
           onCancel={() => onCloseModal()}
           okText={"Invia " + currentVariables.text}
           okButtonProps={{ disabled: docList.length === 0 }}
           destroyOnClose
           onOk={() => onConfirmModal()}
    >
      <div>Allegare {currentVariables.text}</div>
      <Upload
        className={"upload-doc"}
        multiple={false}
        accept={"application/pdf, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/vnd.oasis.opendocument.text, .zip,application/zip,application/x-zip-compressed,application/x-compressed"}
        onChange={(info) => {
          handleOnChange(info.fileList);
        }}
        onRemove={file => {
          const index = docList.findIndex(el => el.uid === file.uid);
          const newFileList = docList.slice();
          newFileList.splice(index, 1);
          setDocList(newFileList);
        }}
        beforeUpload={(file, fileList) => {
          let _file = {
            ...file as object,
            name: file.name,
            fileName: file.name,
            content_type: file.type
          } as unknown as UploadDoc;
          const isBelowMaxSize = file.size <= maxFileSize;
          if (!isBelowMaxSize) {
            setFilesTooLong(prevState => {
              return [...prevState, file.name];
            });
          } else
            setDocList(prevState => [...prevState, _file]);
          return false;
        }}
      >
        {docList.length === 0 && <Button
          style={{ width: "100%", marginTop: "10px", border: "1px dashed" }}
          icon={<UploadOutlined />}
        >
          Seleziona o trascina documenti
        </Button>}
      </Upload>
      <i>Estensioni supportate: .pdf, .doc, .docx, .odt</i>
      {filesTooLong.length !== 0 && <div>
        <Divider />
        <Row justify={"center"} style={{ marginBottom: "15px" }}><h3>I seguenti documenti non verranno caricati</h3>
        </Row>
        {filesTooLong.map(el => {
          return <Row justify={"space-between"}>
            <Col span={12}>
              <div className={"ellipsis-name"}>{el}</div>
            </Col>
            <Col span={12}>
              <div className={"ellipsis-name"}><CloseCircleOutlined style={{ color: "red", marginRight: "5px" }} />Dimensione
                massima superata
              </div>
            </Col>
          </Row>;
        })}
      </div>}
      <div style={{ marginTop: "1rem" }}>
        Commento:
      </div>
      <Input.TextArea value={currentNote} onChange={e => setCurrentNote(e.target.value)} />
    </Modal>
    <Button
      type={"primary"}
      ghost
      {...buttonProps}
      disabled={isButtonDisabled || buttonProps?.disabled}
      style={{ width: "100%", ...buttonProps?.style }}
      onClick={(e) => {
        buttonProps?.onClick?.(e);
        setIsModalOpen(true);
      }}>
      Invia {currentVariables.text}
    </Button>
  </>;
}